import { useOktaAuth } from '@okta/okta-react';
import { oktaAuth } from '@/config/okta-config';

/**
 * Get access token from Okta (React hook)
 * This should be called from within a component that has OktaProvider
 */
export function useAccessToken(): string | null {
  const { authState } = useOktaAuth();
  return authState?.accessToken?.accessToken ?? null;
}

/**
 * Get ID token synchronously (for use outside React components)
 * Gets the ID token from Okta's token manager
 * ID token contains user identity information (email, name, etc.) which the backend expects
 */
export async function getIdToken(): Promise<string | null> {
  // Primary method: try sessionStorage first (most reliable for Okta React SDK)
  try {
    const tokenStorage = sessionStorage.getItem('okta-token-storage');
    if (tokenStorage) {
      const parsed = JSON.parse(tokenStorage);
      // Try ID token first (contains user info like email, name - what backend expects)
      if (parsed?.idToken?.idToken && typeof parsed.idToken.idToken === 'string') {
        return parsed.idToken.idToken;
      }
      if (parsed?.idToken?.value && typeof parsed.idToken.value === 'string') {
        return parsed.idToken.value;
      }
    }
  } catch (error) {
    console.warn('getIdToken: Error parsing token from sessionStorage', error);
  }
  
  // Secondary method: try token manager
  if (oktaAuth) {
    try {
      const tokenManager = oktaAuth.tokenManager;
      const idToken = await tokenManager.get('idToken');
      if (idToken) {
        const tokenValue = 'value' in idToken ? (idToken as { value: string }).value : null;
        if (tokenValue) {
          return tokenValue;
        }
      }
    } catch (error) {
      console.warn('getIdToken: Error getting token from token manager', error);
    }
  }
  
  // Last resort: try localStorage
  try {
    const tokenStorage = localStorage.getItem('okta-token-storage');
    if (tokenStorage) {
      const parsed = JSON.parse(tokenStorage);
      if (parsed?.idToken?.idToken && typeof parsed.idToken.idToken === 'string') {
        return parsed.idToken.idToken;
      }
      if (parsed?.idToken?.value && typeof parsed.idToken.value === 'string') {
        return parsed.idToken.value;
      }
    }
  } catch (error) {
    console.warn('getIdToken: Error parsing token from localStorage', error);
  }
  
  return null;
}

/**
 * Get access token synchronously (for use outside React components)
 */
export async function getAccessToken(): Promise<string | null> {
  // Backend expects ID token (with user info), so prioritize ID token
  const idToken = await getIdToken();
  if (idToken) {
    return idToken;
  }
  
  // Fallback to access token if ID token not available
  // Primary method: try sessionStorage first
  try {
    const tokenStorage = sessionStorage.getItem('okta-token-storage');
    if (tokenStorage) {
      const parsed = JSON.parse(tokenStorage);
      if (parsed?.accessToken?.accessToken && typeof parsed.accessToken.accessToken === 'string') {
        return parsed.accessToken.accessToken;
      }
      if (parsed?.accessToken?.value && typeof parsed.accessToken.value === 'string') {
        return parsed.accessToken.value;
      }
    }
  } catch (error) {
    console.warn('getAccessToken: Error parsing token from sessionStorage', error);
  }
  
  // Secondary method: try token manager
  if (!oktaAuth) return null;
  try {
    const tokenManager = oktaAuth.tokenManager;
    const accessToken = await tokenManager.get('accessToken');
    if (accessToken) {
      const tokenValue = 'value' in accessToken ? (accessToken as { value: string }).value : null;
      if (tokenValue) {
        return tokenValue;
      }
    }
  } catch (error) {
    console.warn('getAccessToken: Error getting token from token manager', error);
  }
  
  // Last resort: try localStorage
  try {
    const tokenStorage = localStorage.getItem('okta-token-storage');
    if (tokenStorage) {
      const parsed = JSON.parse(tokenStorage);
      if (parsed?.accessToken?.accessToken && typeof parsed.accessToken.accessToken === 'string') {
        return parsed.accessToken.accessToken;
      }
      if (parsed?.accessToken?.value && typeof parsed.accessToken.value === 'string') {
        return parsed.accessToken.value;
      }
    }
  } catch (error) {
    console.warn('getAccessToken: Error parsing token from localStorage', error);
  }
  
  return null;
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated(): boolean {
  try {
    const token = sessionStorage.getItem('okta-token-storage');
    if (token) {
      const parsed = JSON.parse(token);
      return !!parsed?.accessToken?.accessToken;
    }
  } catch {
    // Ignore errors
  }
  return false;
}

export default {
  useAccessToken,
  getAccessToken,
  getIdToken,
  isAuthenticated,
};
